import { RotateCcw } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { ApiError } from '../../../../lib/apiError';
import { PpmpDepartmentModal } from '../components/PpmpDepartmentModal';
import { ppmpDepartmentsApi } from '../procurementPlansApi';
import type { PpmpDepartmentListItem } from '../procurementPlanTypes';

/**
 * Which organizational units may file a PPMP, reached through **Procurement → Procurement
 * Planning → Settings**.
 *
 * It is the DENOMINATOR on every annual procurement plan's roster: the plan lists one row per
 * enrolled department, whether or not it has filed anything, and that is how a reader sees who
 * has NOT started. An explicit list rather than a rule over the chart's tiers — see
 * `PpmpDepartment` for why, and for why it is a SECOND list rather than a reading of the budget
 * one.
 */
export function PpmpDepartmentsIndexPage() {
  const navigate = useNavigate();

  const [creating, setCreating] = useState(false);
  const [confirming, setConfirming] = useState<{
    kind: 'deactivate' | 'activate';
    department: PpmpDepartmentListItem;
  } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  const fetchDepartments = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      ppmpDepartmentsApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
        },
        signal,
      ),
    [],
  );

  /*
    NO Edit entry, and the absence is the design: the row holds a link to an org unit and
    nothing else, so there is nothing on it to correct. Moving the roster to a different office
    is a withdrawal and an enrolment, which are the two entries this menu has.
  */
  const rowActions = useCallback(
    (department: PpmpDepartmentListItem): MenuItem[] => [
      {
        key: `restore-${department.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: 'ppmp-departments.delete',
        disabledReason: department.isActive ? 'This department is already enrolled.' : null,
        onClick: () => setConfirming({ kind: 'activate', department }),
      },
    ],
    [],
  );

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'deactivate') {
        await ppmpDepartmentsApi.deactivate(confirming.department.id);
      } else {
        await ppmpDepartmentsApi.activate(confirming.department.id);
      }

      setConfirming(null);
      refresh();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0),
      );
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <IndexPage<PpmpDepartmentListItem>
        module="ppmp-departments"
        breadcrumb={[
          'Procurement',
          'Procurement Planning',
          { label: 'Settings', to: '/procurement/procurement-planning/settings' },
          'PPMP Departments',
        ]}
        columns={COLUMNS}
        facets={[{ key: 'status', label: 'Status' }]}
        auditEntityTypes={['PpmpDepartment']}
        rowKey={(department) => department.id}
        fetch={fetchDepartments}
        reloadKey={reloadKey}
        searchPlaceholder="Search departments"
        emptyMessage="No PPMP departments yet. Enrol the offices that file a procurement plan — every plan's roster is built from this list."
        onCreate={() => setCreating(true)}
        createPermission="ppmp-departments.create"
        onRowOpen={(department) =>
          navigate(`/procurement/procurement-planning/settings/ppmp-departments/${department.id}`)
        }
        openPermission="ppmp-departments.read"
        rowActions={rowActions}
        onDelete={(department) => setConfirming({ kind: 'deactivate', department })}
        deletePermission="ppmp-departments.delete"
        deleteLabel={() => 'Withdraw'}
        deleteDisabledReason={(department) =>
          department.isActive ? null : 'This department is already withdrawn.'
        }
        onClearData={() => ppmpDepartmentsApi.clear()}
        clearDataPermission="ppmp-departments.clear-data"
        otherInfo={(department) => department}
      />

      {creating ? (
        <PpmpDepartmentModal
          open
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            refresh();
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={confirming.kind === 'deactivate' ? 'Withdraw department' : 'Restore department'}
          danger={confirming.kind === 'deactivate'}
          requireConfirm={confirming.kind === 'deactivate'}
          requireCredential={confirming.kind === 'deactivate'}
          loading={running}
          id={`ppmp-department-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {confirming.kind === 'deactivate'
                  ? `${confirming.department.orgUnitName} stops appearing on every procurement plan's roster, and stops counting toward how many departments are expected to file.`
                  : `${confirming.department.orgUnitName} appears on every procurement plan's roster again.`}
              </p>

              {/*
                The count is stated because withdrawing does LESS than it sounds like: it says
                the office no longer FILES a procurement plan, not that what it filed never
                happened. A reader who expected the PPMPs to go would otherwise see nothing
                change on the plans.
              */}
              {confirming.kind === 'deactivate' && confirming.department.ppmpCount > 0 ? (
                <p>
                  The {confirming.department.ppmpCount} PPMP
                  {confirming.department.ppmpCount === 1 ? '' : 's'} it has already filed are kept
                  and stay on the plans they belong to — withdrawing a department is not a
                  statement that its procurement was never planned.
                </p>
              ) : null}

              {confirming.kind === 'activate' ? (
                <p>
                  Refused if the unit has been enrolled again meanwhile — a withdrawal frees it.
                </p>
              ) : null}

              {error ? (
                <p
                  role="alert"
                  className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700"
                >
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

const COLUMNS: ColumnDef<PpmpDepartmentListItem>[] = [
  {
    key: 'orgUnitName',
    header: 'Department',
    width: 280,
    flex: true,
    sortable: true,
    render: (department) => <TextCell value={department.orgUnitName} />,
  },
  {
    key: 'orgUnitTypeName',
    header: 'Tier',
    width: 160,
    summary: true,
    /*
      The unit's TIER, and it is gated: it belongs to the org-unit-types module, which this
      reader may not hold. The department NAME beside it is not gated — a reader of this roster
      is entitled to the name of what they enrolled.
    */
    permission: 'org-unit-types.read',
    render: (department) => <TextCell value={department.orgUnitTypeName} />,
  },
  {
    key: 'orgUnitCode',
    header: 'Unit code',
    width: 140,
    summary: true,
    // A PATH through the chart (`0001-2-3`), so mono and never numeric — an identifier, and
    // nothing in it should dim.
    permission: 'org-units.read',
    render: (department) => <TextCell value={department.orgUnitCode} mono />,
  },
  {
    key: 'ppmpCount',
    header: 'PPMPs filed',
    width: 120,
    align: 'right',
    /*
      NOT sortable: the server's allow-list deliberately omits it, because ordering a page by a
      correlated count makes the database compute one per row before it can page. A header that
      offered the sort would fall back silently, which reads as a broken control.

      AND NOT A SUMMARY VALUE. The list view concatenates summary values with NO LABELS, so a
      count arrives as a bare `· 0` at the end of the line — a number a reader cannot interpret
      without the header the column has and that line does not. The tier and the unit code read
      on their own; a count does not.
    */
    permission: 'ppmps.read',
    render: (department) => <TextCell value={String(department.ppmpCount)} mono numeric />,
  },
  {
    key: 'status',
    header: 'Status',
    width: 140,
    status: true,
    render: (department) => {
      /*
        TWO different absences, and collapsing them would hide the one that needs acting on: a
        WITHDRAWN department was taken off the roster deliberately, while an enrolled one whose
        office has left the chart is a roster row pointing at nothing — which nobody chose and
        somebody should fix.
      */
      if (!department.isActive) {
        return <StatusText tone="inactive">Withdrawn</StatusText>;
      }

      return department.isOrgUnitActive ? (
        <StatusText tone="active">Enrolled</StatusText>
      ) : (
        <StatusText tone="blocked">Unit deleted</StatusText>
      );
    },
  },
];
